import { ReactElement, useMemo, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import CollapsibleText from 'modules/common/content/components/common/reviews-analysis/collapsible-text';
import ReviewsViewer from 'modules/common/content/components/common/reviews-analysis/reviews-viewer';

interface SeasonalTrendDetailsProps {
  trendName: string;
  reviewsAnalysis: Record<string, unknown>;
}

const formatKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^\w/, c => c.toUpperCase());
};

export function SeasonalTrendDetails({ trendName, reviewsAnalysis }: SeasonalTrendDetailsProps): ReactElement | null {
  const [expanded, setExpanded] = useState(false);

  const trendDetails = useMemo(() => {
    const seasonalTrends = reviewsAnalysis?.seasonalTrendsAnalysis as
      | { trends?: Array<Record<string, unknown>> }
      | undefined;

    if (!seasonalTrends?.trends?.length) return null;

    const match = seasonalTrends.trends.find(trend => trend.name === trendName);
    if (!match) return null;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { name: _, ...rest } = match;
    return Object.keys(rest).length > 0 ? rest : null;
  }, [trendName, reviewsAnalysis]);

  if (!trendDetails) return null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        component="button"
        variant="caption"
        onClick={() => setExpanded(!expanded)}
        sx={{ mt: 0.5, cursor: 'pointer', display: 'block' }}
      >
        {expanded ? 'Hide Details' : 'Show Details'}
      </Link>
      {expanded &&
        Object.entries(trendDetails).map(([key, value]) => {
          const isComplex =
            (typeof value === 'object' && value !== null && !Array.isArray(value)) ||
            (Array.isArray(value) && value.some(item => typeof item === 'object' && item !== null));

          if (isComplex) {
            return (
              <Box key={key} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  <strong>{formatKey(key)}:</strong>
                </Typography>
                <Box sx={{ pl: 1 }}>
                  {Array.isArray(value) ? (
                    <ReviewsViewer data={{ [key]: value } as Record<string, unknown>} />
                  ) : (
                    <ReviewsViewer data={value as Record<string, unknown>} />
                  )}
                </Box>
              </Box>
            );
          }

          if (typeof value === 'boolean') {
            return (
              <Typography key={key} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                <strong>{formatKey(key)}:</strong> {value ? 'Yes' : 'No'}
              </Typography>
            );
          }

          const displayValue = Array.isArray(value) ? value.join(', ') : String(value ?? '');
          if (!displayValue) return null;

          if (typeof value === 'string' && value.length > 80) {
            return (
              <Box key={key} sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  <strong>{formatKey(key)}:</strong>
                </Typography>
                <CollapsibleText text={value} maxLength={80} />
              </Box>
            );
          }

          return (
            <Typography key={key} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
              <strong>{formatKey(key)}:</strong> {displayValue}
            </Typography>
          );
        })}
    </>
  );
}
